import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  Fade,
  Grow,
} from "@mui/material";
import {
  Download,
  Description,
  Work,
  School,
  Star,
  Code,
} from "@mui/icons-material";
import styled from "styled-components";
import resumePdf from "../resources/resume.pdf";

const HeroSection = styled.section`
  background: linear-gradient(135deg, #0066cc 0%, #008cff 100%);
  color: white;
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const ResumeContainer = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 2rem 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 3rem;
`;

const FeatureCard = styled(Paper)`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  min-height: 210px;
  padding: 2rem;
  text-align: center;
  border-radius: 8px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 600px) {
    min-height: 180px;
    padding: 1.5rem 1.25rem;
  }
`;

const ActionButton = styled(Button)`
  margin: 0.5rem;
  padding: 12px 32px;
  border-radius: 50px;
  text-transform: none;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;

function Resume() {
  const [loading, setLoading] = useState(true);

  const iframeStyle = {
    width: "100%",
    border: "none",
    height: "80vh",
    display: loading ? "none" : "block",
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = resumePdf;
    link.download = "Cory_Smith_Resume.pdf";
    link.click();
  };

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <Fade in timeout={1000}>
            <Box textAlign="center">
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 700,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Resume
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.9,
                  maxWidth: "600px",
                  margin: "0 auto",
                  lineHeight: 1.6,
                }}
              >
                Professional experience, skills, and achievements
              </Typography>
            </Box>
          </Fade>
        </Container>
      </HeroSection>

      {/* Resume Preview Section */}
      <Container sx={{ py: { xs: 4, md: 8 } }}>
        <Grow in timeout={1200}>
          <Box sx={{ mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h4"
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: { xs: 2.5, md: 3 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Description sx={{ color: "#0066CC" }} />
              Overview
            </Typography>

            <Grid
              container
              rowSpacing={{ xs: 2, sm: 3 }}
              columnSpacing={{ xs: 0, sm: 3 }}
              sx={{ mb: { xs: 2, sm: 4 } }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <FeatureCard elevation={2}>
                  <Work sx={{ fontSize: 40, color: "#0066CC", mb: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Experience
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    5+ years in application development
                  </Typography>
                </FeatureCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FeatureCard elevation={2}>
                  <Code sx={{ fontSize: 40, color: "#0066CC", mb: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Skills
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Full-stack development expertise
                  </Typography>
                </FeatureCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FeatureCard elevation={2}>
                  <School sx={{ fontSize: 40, color: "#0066CC", mb: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Education
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Professional certifications
                  </Typography>
                </FeatureCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FeatureCard elevation={2}>
                  <Star sx={{ fontSize: 40, color: "#0066CC", mb: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Achievements
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Multiple successful projects
                  </Typography>
                </FeatureCard>
              </Grid>
            </Grid>
          </Box>
        </Grow>

        {/* Action Buttons */}
        <Fade in timeout={1400}>
          <Box
            sx={{
              textAlign: "center",
              mb: 6,
              mt: { xs: 5, md: 10 },
            }}
          >
            <ActionButton
              variant="contained"
              size="large"
              startIcon={<Download />}
              onClick={handleDownload}
              sx={{
                backgroundColor: "#0066CC",
                "&:hover": {
                  backgroundColor: "#004C99",
                },
              }}
            >
              Download PDF
            </ActionButton>
          </Box>
        </Fade>

        {/* Resume Viewer */}
        <Grow in timeout={1600}>
          <ResumeContainer sx={{ mt: 2 }}>
            {loading && (
              <LoadingContainer>
                <CircularProgress size={60} sx={{ color: "#0066CC", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Loading Resume...
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Please wait while we prepare your resume for viewing
                </Typography>
              </LoadingContainer>
            )}

            <iframe
              src={resumePdf}
              style={iframeStyle}
              onLoad={handleLoad}
              title="Cory Smith Resume"
            />
          </ResumeContainer>
        </Grow>
      </Container>
    </Box>
  );
}

export default Resume;
